import {
  DatePicker,
  Divider,
  Flex,
  Form,
  FormInstance,
  Row,
  Select,
  Typography,
} from "antd";

import "./ProjectSalesInformation.scss";
import { ProjectFormFieldItem } from "../project-form-field-item/ProjectFormFieldItem";
import { SalesConfigurationSelector } from "../sales-configuration-selector/SalesConfigurationSelector";
import { SalesConfigurationDisplay } from "../sales-configuration-selector/SalesConfigurationDisplay";
import { isEmpty } from "lodash";
import dayjs from "dayjs";
import { useDisplayFieldByStage } from "projects/hooks";
import { DateFormat } from "shared/constants";
import { MoneyInput } from "shared/components";
import { useBoundStore } from "store";
import { UserRoles } from "admin/constants";
import { useSalesConfigurations } from "sales/queries";

type Props = {
  form: FormInstance;
  currentStage: string;
  isEditing: boolean;
};

const fieldCategory = "salesInformationFields";

export const ProjectSalesInformation = ({
  form,
  currentStage,
  isEditing,
}: Props) => {
  const validationStage = Form.useWatch("stage", form);
  const salesConfigurationId = Form.useWatch("salesConfigurationId", form);
  const hasStageChanged = form.isFieldTouched("stage");
  const { displayFieldCategoryByStage } = useDisplayFieldByStage();
  const selectPlaceholder = isEditing ? "Select..." : "None";
  const datePlaceholder = isEditing ? "Select date" : "None";
  const moneyPlaceholder = isEditing ? undefined : "None";

  const { user } = useBoundStore();
  const { data: salesConfigurations } = useSalesConfigurations();
  const selectedConfig = salesConfigurations?.find((c) => c.id === salesConfigurationId);
  const isOwner = user?.role === UserRoles.Owner;
  const isAccountManager = !!(user?.id && selectedConfig?.accountManagerId === user.id);
  const canViewCommissions = isOwner || isAccountManager;

  const percentToCloseOptions = [
    { value: "green_75_100", label: "Green 75%-100%" },
    { value: "yellow_35_75", label: "Yellow 35%-75%" },
    { value: "red_lt_35", label: "Red < 35%" },
    { value: "tbd", label: "TBD" },
    { value: "not_applicable", label: "Not Applicable" },
  ];

  return displayFieldCategoryByStage(fieldCategory, currentStage) ? (
    <div className='project-sales-information-container'>
      <Flex>
        <Typography.Title className='project-sales-information-title' level={2}>
          Sales Information
        </Typography.Title>
      </Flex>
      <Divider className='project-sales-information-divider' />
      <Row justify='start' gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
        {/* Sales Configuration Selector */}
        <ProjectFormFieldItem
          name='salesConfigurationId'
          label='Sales Configuration'
          fieldCategory={fieldCategory}
          colSpan={12}
          validationStage={validationStage}
          projectStage={currentStage}
          hasStageChanged={hasStageChanged}
        >
          {isEditing ? (
            <SalesConfigurationSelector size='small' />
          ) : (
            <SalesConfigurationDisplay />
          )}
        </ProjectFormFieldItem>

        <ProjectFormFieldItem
          name='percentToClose'
          label='% to Close'
          fieldCategory={fieldCategory}
          colSpan={6}
          validationStage={validationStage}
          projectStage={currentStage}
          hasStageChanged={hasStageChanged}
        >
          <Select
            size='small'
            options={[
              { value: "", label: selectPlaceholder },
              ...percentToCloseOptions,
            ]}
            onSelect={(value) => {
              form.setFieldValue("percentToClose", value);
            }}
            placeholder={selectPlaceholder}
          />
        </ProjectFormFieldItem>

        {/* Commission Amount fields - only visible to Owner and Account Manager */}
        {canViewCommissions && (
          <>
            <ProjectFormFieldItem
              name='accountManagerCommissionAmount'
              label='Account Manager Commission Amount'
              fieldCategory={fieldCategory}
              colSpan={6}
              validationStage={validationStage}
              projectStage={currentStage}
              hasStageChanged={hasStageChanged}
            >
              <MoneyInput
                size='small'
                readOnly
                disabled
                placeholder={moneyPlaceholder}
              />
            </ProjectFormFieldItem>
            <ProjectFormFieldItem
              name='leadGeneratorCommissionAmount'
              label='Lead Generator Commission Amount'
              fieldCategory={fieldCategory}
              colSpan={6}
              validationStage={validationStage}
              projectStage={currentStage}
              hasStageChanged={hasStageChanged}
            >
              <MoneyInput
                size='small'
                readOnly
                disabled
                placeholder={moneyPlaceholder}
              />
            </ProjectFormFieldItem>
            <ProjectFormFieldItem
              name='salesSupportCommissionAmount'
              label='Sales Support Commission Amount'
              fieldCategory={fieldCategory}
              colSpan={6}
              validationStage={validationStage}
              projectStage={currentStage}
              hasStageChanged={hasStageChanged}
            >
              <MoneyInput
                size='small'
                readOnly
                disabled
                placeholder={moneyPlaceholder}
              />
            </ProjectFormFieldItem>
            <ProjectFormFieldItem
              name='commissionPaid'
              label='Commission Paid'
              fieldCategory={fieldCategory}
              colSpan={6}
              validationStage={validationStage}
              projectStage={currentStage}
              hasStageChanged={hasStageChanged}
            >
              <DatePicker
                allowClear
                size='small'
                placeholder={datePlaceholder}
                onChange={(x) => {
                  form.setFieldValue(
                    "commissionPaid",
                    isEmpty(x) ? null : dayjs(x, DateFormat)
                  );
                }}
                format={DateFormat}
              />
            </ProjectFormFieldItem>
          </>
        )}
      </Row>
    </div>
  ) : null;
};
